import React, { useState, useMemo } from "react";
import {
  Page,
  EmptyState,
  ItemTable,
  ItemTableSearch,
  ItemTableBody,
  ItemTableRow,
  ItemTableCell,
  ItemTableAvatar,
  ItemTableTitle,
  ItemTableSubtitle,
  ItemTableActions,
  ItemTableLoading,
  ItemTableEmpty,
  Badge,
} from "@ui";
import { useTools } from "@agentic_system/api/tool";
import { ToolEditDrawer } from "./edit-drawer";
import { Wrench, Pencil } from "lucide-react";

export function Tools() {
  const { data: tools, isLoading } = useTools();
  const [editingToolId, setEditingToolId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Client-side filtering
  const filteredTools = useMemo(() => {
    if (!tools) return [];
    if (!searchQuery.trim()) return tools;

    const query = searchQuery.toLowerCase();
    return tools.filter(
      (tool) =>
        tool.label.toLowerCase().includes(query) ||
        tool.display_name?.toLowerCase().includes(query) ||
        tool.identifier.toLowerCase().includes(query) ||
        tool.description?.toLowerCase().includes(query),
    );
  }, [tools, searchQuery]);

  const hasTools = (tools?.length ?? 0) > 0;

  return (
    <Page
      data-testid="tools"
      title="Outils"
      description="Configurez les outils que les agents peuvent utiliser"
    >
      {isLoading ? (
        <ItemTable>
          <ItemTableLoading rows={4} />
        </ItemTable>
      ) : hasTools ? (
        <ItemTable>
          <ItemTableSearch
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Rechercher des outils..."
          />
          <ItemTableBody>
            {filteredTools.length > 0 ? (
              filteredTools.map((tool) => (
                <ItemTableRow
                  key={tool.id}
                  onClick={() => setEditingToolId(tool.id)}
                  className="grid-cols-[1fr_auto_auto]"
                >
                  <ItemTableCell>
                    <ItemTableAvatar icon={Wrench} />
                    <div className="min-w-0">
                      <ItemTableTitle>
                        <span className="flex items-center gap-2">
                          {tool.display_name || tool.label}
                          <code className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded text-slate-600">
                            {tool.identifier}
                          </code>
                        </span>
                      </ItemTableTitle>
                      <ItemTableSubtitle>
                        {tool.description || "Aucune description"}
                      </ItemTableSubtitle>
                    </div>
                  </ItemTableCell>
                  <ItemTableCell>
                    <Badge
                      label={tool.enabled ? "Activé" : "Désactivé"}
                      color={tool.enabled ? "green" : "default"}
                      size="sm"
                    />
                  </ItemTableCell>
                  <ItemTableCell>
                    <ItemTableActions
                      items={[
                        {
                          icon: <Pencil className="w-4 h-4" />,
                          label: "Modifier les descriptions",
                          onClick: () => setEditingToolId(tool.id),
                        },
                      ]}
                    />
                  </ItemTableCell>
                </ItemTableRow>
              ))
            ) : (
              <ItemTableEmpty
                title="Aucun outil trouvé"
                description={`Aucun outil ne correspond à "${searchQuery}"`}
              />
            )}
          </ItemTableBody>
        </ItemTable>
      ) : (
        <EmptyState
          icon={Wrench}
          title="Aucun outil configuré"
          description="Les outils sont synchronisés automatiquement depuis le code. Exécutez `rake agentic:tools:sync` pour les charger."
        />
      )}

      <ToolEditDrawer
        toolId={editingToolId}
        onClose={() => setEditingToolId(null)}
      />
    </Page>
  );
}
